import { useCallback, useEffect, useRef, useState } from "react";

const REFRESH_MS = 3000;
const IDLE_MS = 4000;
const MAX_LIFETIME_MS = 6000;
const ASSISTANT_LIFETIME_MS = 30000;
const ASSISTANT_MIN = 1n << 62n;

/** Assistant seats sit above 2^62; their composing hint lasts a whole model call. */
export const isAssistantId = (memberId: string) =>
  /^[1-9]\d{0,18}$/.test(memberId) && BigInt(memberId) >= ASSISTANT_MIN;

/** Send only typing transitions and bounded refreshes; never message contents. */
export function useTypingIndicator(
  send: (active: boolean) => void,
  enabled: boolean,
) {
  const active = useRef(false);
  const lastSent = useRef(0);
  const idle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stop = useCallback(() => {
    clearTimeout(idle.current);
    if (active.current) send(false);
    active.current = false;
    lastSent.current = 0;
  }, [send]);
  useEffect(() => {
    const hidden = () => {
      if (document.visibilityState === "hidden") stop();
    };
    window.addEventListener("blur", stop);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      stop();
      window.removeEventListener("blur", stop);
      document.removeEventListener("visibilitychange", hidden);
    };
  }, [enabled, stop]);
  return useCallback(
    (typing: boolean) => {
      if (!enabled || !typing || document.visibilityState === "hidden") {
        stop();
        return;
      }
      const now = Date.now();
      if (!active.current || now - lastSent.current >= REFRESH_MS) {
        send(true);
        active.current = true;
        lastSent.current = now;
      }
      clearTimeout(idle.current);
      idle.current = setTimeout(stop, IDLE_MS);
    },
    [enabled, send, stop],
  );
}

/** Received indicators are ephemeral and scoped to the current conversation. */
export function useTypingMembers(scope: string, chatId: string, self: string) {
  const [state, setState] = useState<{ scope: string; members: string[] }>({
    scope,
    members: [],
  });
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const clear = useCallback(() => {
    for (const timer of timers.current.values()) clearTimeout(timer);
    timers.current.clear();
    setState({ scope, members: [] });
  }, [scope]);
  const clearAssistants = useCallback(() => {
    for (const [memberId, timer] of timers.current) {
      if (!isAssistantId(memberId)) continue;
      clearTimeout(timer);
      timers.current.delete(memberId);
    }
    setState((previous) => ({
      scope,
      members:
        previous.scope === scope
          ? previous.members.filter((value) => !isAssistantId(value))
          : [],
    }));
  }, [scope]);
  useEffect(() => {
    const ownedTimers = timers.current;
    return () => {
      for (const timer of ownedTimers.values()) clearTimeout(timer);
      ownedTimers.clear();
    };
  }, [scope]);
  const receive = useCallback(
    (frame: Record<string, unknown>) => {
      const memberId = frame.memberId;
      if (
        frame.v !== 1 ||
        frame.type !== "typing" ||
        frame.chatId !== chatId ||
        typeof memberId !== "string" ||
        !/^[1-9]\d{0,18}$/.test(memberId) ||
        memberId === self ||
        (frame.state !== "start" && frame.state !== "stop") ||
        typeof frame.expiresInMs !== "number" ||
        !Number.isFinite(frame.expiresInMs)
      )
        return;
      const remove = () => {
        timers.current.delete(memberId);
        setState((previous) => ({
          scope,
          members:
            previous.scope === scope
              ? previous.members.filter((value) => value !== memberId)
              : [],
        }));
      };
      clearTimeout(timers.current.get(memberId));
      if (frame.state === "stop" || frame.expiresInMs <= 0) {
        remove();
        return;
      }
      setState((previous) => ({
        scope,
        members: [
          ...new Set([
            ...(previous.scope === scope ? previous.members : []),
            memberId,
          ]),
        ],
      }));
      const lifetime = isAssistantId(memberId)
        ? ASSISTANT_LIFETIME_MS
        : MAX_LIFETIME_MS;
      timers.current.set(
        memberId,
        setTimeout(remove, Math.min(lifetime, frame.expiresInMs)),
      );
    },
    [chatId, scope, self],
  );
  return {
    members: state.scope === scope ? state.members : [],
    receive,
    clear,
    clearAssistants,
  };
}
